import blackArrowIcon from "assets/images/black-arrow-icon.svg";
import classNames from "classnames";
import {format} from "date-fns";
import {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {appointments as strings, common as stringsCommon} from "strings";

import AlertModal from "components/AlertModal";
import AppointmentConfirmationModal from "components/AppointmentConfirmationModal";
import Button from "components/Button";
import Divider from "components/Divider";
import LoadingIndicator from "components/LoadingIndicator";
import PageTitle from "components/PageTitle";
import TitlePatientNameSelect from "components/TitlePatientNameSelect";

import * as models from "liberty-core/api/models";
import {
  useGetAppointmentCampaigns,
  useGetAppointments,
} from "liberty-core/api/react-query/appointment";

import {useAuthenticatedUser} from "lib/auth";
import useCurrentPatientId from "lib/useCurrentPatientId";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./AppointmentsPage.module.css";

function AppointmentInfo({
  appointment,
  onPress,
}: {
  appointment: models.Appointment;
  onPress: () => void;
}) {
  return (
    <button className={styles.appointmentInfo} onClick={onPress}>
      <div className={styles.appointmentRow}>
        <div>
          <div className={styles.appointmentName}>
            {appointment.optionName} - {appointment.campaignName}
          </div>
          <div className={styles.appointmentDescription}>
            {format(
              new Date(appointment.scheduledDateTime),
              "EEEE MM/dd/yy h:mmaaa",
            )}
          </div>
        </div>
        <img className={styles.blackArrow} src={blackArrowIcon} alt="" />
      </div>
    </button>
  );
}

type LocationState = {
  startDateTime?: string;
  successMessage?: string;
  invalidCampaignOrOptionIds?: boolean;
};

export default function AppointmentsPage() {
  const location = useLocation();
  const locationState = location.state as LocationState;
  const successMessage = useMemo(
    () => locationState?.successMessage,
    [locationState],
  );
  const startDateTime = useMemo(
    () => locationState?.startDateTime,
    [locationState],
  );
  const [
    showInvalidCampaignOrOptionIdModal,
    setShowInvalidCampaignOrOptionIdModal,
  ] = useState(!!locationState?.invalidCampaignOrOptionIds);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  useEffect(() => {
    if (startDateTime || successMessage) {
      setShowConfirmationModal(true);
    }
  }, [startDateTime, successMessage]);

  const [pharmacy] = useCurrentPharmacy();
  const navigate = useNavigate();
  const [authenticatedUser] = useAuthenticatedUser();
  const {selectedPatientId, updateSelectedPatientId} = useCurrentPatientId(
    authenticatedUser?.data.linkedPatients ?? [],
  );
  const {
    data: allAppts,
    isLoading: isLoadingAppts,
    isRefetching,
  } = useGetAppointments(selectedPatientId);

  const {data: apptCampaigns, isLoading: isLoadingApptCampaigns} =
    useGetAppointmentCampaigns(pharmacy?.id ?? 0);
  const noApptOptions =
    !apptCampaigns || !apptCampaigns.find((c) => c.appointmentOptions?.length);

  return (
    <div>
      {authenticatedUser?.data.linkedPatients?.length ? (
        <>
          <PageTitle
            className={classNames([styles.titleText, styles.smallTitleText])}
          >
            {strings.title}
          </PageTitle>
          <TitlePatientNameSelect
            defaultPatientName={authenticatedUser.data.name}
            linkedPatients={authenticatedUser.data.linkedPatients}
            selectedPatientId={selectedPatientId}
            onChange={updateSelectedPatientId}
            className={styles.patientSelect}
          />
        </>
      ) : (
        <PageTitle className={styles.titleText}>{strings.title}</PageTitle>
      )}
      <Divider className={styles.topDivider} />
      <div className={styles.appointmentsContainer}>
        {isLoadingAppts || isRefetching ? (
          <LoadingIndicator />
        ) : allAppts && allAppts.length > 0 ? (
          allAppts?.map((appt) => (
            <div key={appt.appointmentId}>
              <AppointmentInfo
                appointment={appt}
                onPress={() =>
                  navigate(`/appointments/${appt.appointmentId}`, {
                    state: {
                      selectedPatientId,
                      campaignName: appt.campaignName,
                      campaignDescription: appt.campaignDescription,
                      optionName: appt.optionName,
                      scheduledDateTime: appt.scheduledDateTime,
                    },
                  })
                }
              />
              <Divider />
            </div>
          ))
        ) : (
          <div className={styles.noApptsText}>
            {strings.noUpcomingAppointments}
          </div>
        )}
      </div>
      <Button
        className={styles.addAppointmentButton}
        variant="filled"
        loading={isLoadingApptCampaigns}
        disabled={noApptOptions}
        onClick={() => {
          const availableCampaigns = apptCampaigns?.filter(
            (c) => !c.deepLinkOnly,
          );
          const uniqueAppointmentTypes =
            availableCampaigns
              ?.map((c) => c.appointmentSettingType)
              .filter(
                (type, i) =>
                  availableCampaigns.findIndex(
                    (c) => c.appointmentSettingType === type,
                  ) === i,
              ) ?? [];
          if (uniqueAppointmentTypes?.length === 1) {
            navigate("/appointments/select-campaign", {
              state: {
                appointmentType: uniqueAppointmentTypes[0],
                selectedPatientId:
                  selectedPatientId ?? authenticatedUser?.data.patientId,
                apptCampaigns: availableCampaigns ?? [],
              },
            });
          } else {
            navigate("/appointments/new", {
              state: {
                selectedPatientId:
                  selectedPatientId ?? authenticatedUser?.data.patientId,
                apptCampaigns: availableCampaigns ?? [],
              },
            });
          }
        }}
      >
        {noApptOptions ? strings.noAppointments : strings.next}
      </Button>
      <AppointmentConfirmationModal
        visible={showConfirmationModal}
        onClickClose={() => setShowConfirmationModal(false)}
        startDateTime={startDateTime ?? ""}
        succeessMessage={successMessage ?? ""}
      />
      <AlertModal
        title={stringsCommon.whoops}
        message={strings.invalid}
        visible={showInvalidCampaignOrOptionIdModal}
        onClickConfirm={() => setShowInvalidCampaignOrOptionIdModal(false)}
      />
    </div>
  );
}
